/**
 * MessageSoundProvider — global sound + tab-flash + OS-notification layer
 * for VOS chat. Founder ask Feb 2026: artists work on background screens
 * (Blender / Maya / Photoshop fullscreen) and miss critical chat pings.
 * Solution mirrors WhatsApp Web behaviour:
 *
 *   • Soft 2-tone "ding" on every incoming channel_message.
 *   • Skip ding when YOU are the author (no own-echo).
 *   • Skip ding when the tab is focused AND you're already viewing
 *     the channel the message was posted to (= you definitely saw it).
 *   • Browser tab title flashes "(3) New message — VawworldOS" while
 *     the tab is hidden, restores once user comes back.
 *   • OS-level Notification (Chrome toast / macOS banner) when tab
 *     is hidden — single permission ask, then silent enroll.
 *   • Mute toggle (vos_mute_chat_sound in localStorage) wired to a
 *     globally exposed window.__vosToggleMute(). The mute button in
 *     the Message Center header reads/writes the same key.
 *
 * Browser autoplay restrictions: the AudioContext starts in 'suspended'
 * state until the user interacts with the page. We resume it on the
 * first click anywhere. From then on, all subsequent dings play silently
 * without any user-gesture requirement.
 *
 * Sound is synthesised at runtime via Web Audio API (no asset file to
 * ship). Two short sine bursts ~520Hz → 660Hz, ~80ms each, soft attack.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import useRealtime from '@/hooks/useRealtime';

const MUTE_KEY = 'vos_mute_chat_sound';
const NOTIFY_KEY = 'vos_notify_chat_os';

// ─── helpers ─────────────────────────────────────────────────────────
function readMute() {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}
function readNotify() {
  try { return localStorage.getItem(NOTIFY_KEY) !== '0'; } catch { return true; }
}

/* getActiveChannelId — the Message Center URL stores the open channel
 * id only inside React state. We read it from a window global the page
 * exposes (see MessageCenterV3.js — it sets window.__vosActiveChannelId).
 * If the user is on a different page, this is null. */
function getActiveChannelId() {
  return (typeof window !== 'undefined' && window.__vosActiveChannelId) || null;
}

// ─── audio engine ────────────────────────────────────────────────────
let _audioCtx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  if (_audioCtx) return _audioCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
  } catch { return null; }
  return _audioCtx;
}

/** Play a soft 2-note ding (520 Hz → 660 Hz). Roughly 200ms total. */
function playDing() {
  const ctx = getCtx();
  if (!ctx) return;
  // Resume — required after a user gesture if browser auto-suspended us.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.18;          // soft, not jarring
  master.connect(ctx.destination);
  const tone = (freq, t0, dur) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Quick attack, gentle release — avoids clicks.
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(1, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.connect(g);
    g.connect(master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  };
  tone(520, now,        0.08);
  tone(660, now + 0.09, 0.12);
}

// ─── tab title flasher ───────────────────────────────────────────────
let _origTitle = null;
let _flashTimer = null;
let _unreadCount = 0;
function flashTitle(plus = 1) {
  if (typeof document === 'undefined') return;
  if (_origTitle == null) _origTitle = document.title || 'VawworldOS';
  _unreadCount += plus;
  if (_flashTimer) return;
  // Alternates between unread badge and the original title every 1.5s,
  // so the founder sees the count even from a glance across the screen.
  let toggle = false;
  _flashTimer = setInterval(() => {
    toggle = !toggle;
    document.title = toggle
      ? `(${_unreadCount}) New message — VawworldOS`
      : (_origTitle || 'VawworldOS');
  }, 1500);
}
function clearTitleFlash() {
  if (_flashTimer) { clearInterval(_flashTimer); _flashTimer = null; }
  _unreadCount = 0;
  if (typeof document !== 'undefined' && _origTitle != null) {
    document.title = _origTitle;
  }
}

// ─── OS-level Notification API ───────────────────────────────────────
function ensureNotifPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'default') {
    // Don't auto-ask — wait until the FIRST attempt to notify, so the
    // browser ties the prompt to actual chat activity (avoids the
    // "annoying" cold-open permission prompt anti-pattern).
  }
}
function fireOSNotification(title, body, onClick) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (!readNotify()) return;
  if (Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body, icon: '/favicon.ico', tag: 'vos-chat',
        silent: false, renotify: true,
      });
      n.onclick = () => {
        try { window.focus(); n.close(); onClick && onClick(); }
        catch { /* ignore */ }
      };
    } catch { /* some browsers throw on missing icon — silent fail */ }
  } else if (Notification.permission === 'default') {
    // First time → request. Subsequent denials → respect silently.
    Notification.requestPermission().then((p) => {
      if (p === 'granted') fireOSNotification(title, body, onClick);
    }).catch(() => {});
  }
}

// ─── React Provider ──────────────────────────────────────────────────
export default function MessageSoundProvider() {
  const { user } = useAuth();
  const lastDingAt = useRef(0);

  // First-click "unlock" — every modern browser requires a user gesture
  // before AudioContext can produce sound. We resume the context on the
  // very first click anywhere; after that, no more friction.
  useEffect(() => {
    const unlock = () => {
      const ctx = getCtx();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock, { once: false });
    document.addEventListener('keydown', unlock, { once: false });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Reset tab title when user returns to the tab.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') clearTitleFlash();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Expose a global toggle so the Message Center mute button stays in
  // sync without prop-drilling a context.
  useEffect(() => {
    window.__vosToggleMute = () => {
      const next = !readMute();
      try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch {}
      // Visual feedback — dispatch a custom event other components listen to.
      window.dispatchEvent(new CustomEvent('vos:mute-changed', { detail: { muted: next } }));
      return next;
    };
    window.__vosIsMuted = readMute;
    ensureNotifPermission();
    return () => {
      delete window.__vosToggleMute;
      delete window.__vosIsMuted;
    };
  }, []);

  const handle = useCallback((event) => {
    if (!event || event.type !== 'channel_message') return;
    const msg = event.message || {};

    // Skip own-echo — the message you just sent fires this event too.
    if (msg.author_user_id && user?.id && msg.author_user_id === user.id) return;

    // Skip thread replies — they fire their own UI badge in the thread
    // panel and would create double-dings on every reply.
    if (msg.reply_to_id) return;

    const muted = readMute();
    const tabHidden = (typeof document !== 'undefined' && document.visibilityState !== 'visible');
    const sameChannelOpen = (event.channel_id && event.channel_id === getActiveChannelId());

    // The "all conditions" branch where we do nothing: user is staring
    // at the channel right now AND the tab is in the foreground. They
    // already see the message land — no need to ding.
    if (sameChannelOpen && !tabHidden) return;

    // Audio
    if (!muted) {
      // De-bounce: max one ding per ~600ms to avoid an "alarm" effect
      // when a sender posts 4 rapid lines in a row.
      const now = Date.now();
      if (now - lastDingAt.current > 600) {
        lastDingAt.current = now;
        try { playDing(); } catch { /* ignore */ }
      }
    }

    // Tab title flash — only when the tab is hidden (visible tabs don't
    // need flashing; the page itself already shows the new message).
    if (tabHidden) {
      flashTitle(1);
      // OS-level notification (Chrome / macOS toast). Permission-gated.
      const authorHandle = msg.author_handle
        || (msg.author_email || '').split('@')[0]
        || msg.author_name
        || 'someone';
      const channelHint = event.channel_slug
        || (event.channel_id || '').replace(/^cross::|^proj::[^:]+::dept::|^proj::[^:]+::|^private::/, '')
        || 'chat';
      fireOSNotification(
        `@${authorHandle} → #${channelHint}`,
        (msg.body || '(attachment)').slice(0, 140),
        () => {
          // Best-effort deep-link to the channel.
          try { window.location.href = `/messages?channel=${encodeURIComponent(event.channel_id || '')}`; }
          catch { /* ignore */ }
        }
      );
    }
  }, [user?.id]);

  useRealtime(handle);
  return null;   // Headless — no UI
}
